import { Authenticated, ErrorComponent } from "@refinedev/core";
import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { SyncrosaleLayout } from "../components/layout/SyncrosaleLayout";
import { SyncrosaleLoader } from "../components/layout/components/SyncrosaleLoader";
import { ErrorBoundary } from "../components/ErrorBoundary";

const CreateStore = lazy(() =>
  import("../pages/stores/create-store/CreateStore").then((m) => ({
    default: m.CreateStore,
  })),
);
const Login = lazy(() =>
  import("../pages/authentication/Login").then((m) => ({ default: m.Login })),
);
const StoreSettings = lazy(() =>
  import("../pages/stores/store-settings/StoreSettings").then((m) => ({
    default: m.StoreSettings,
  })),
);
const Filters = lazy(() =>
  import("../pages/store-security/filters/Filters").then((m) => ({
    default: m.Filters,
  })),
);
const PricingSettings = lazy(() =>
  import("../pages/stores/pricing-settings/PricingSettings").then((m) => ({
    default: m.PricingSettings,
  })),
);
const Inventory = lazy(() =>
  import("../pages/inventory/inventory/Inventory").then((m) => ({
    default: m.Inventory,
  })),
);
const Restrictions = lazy(() =>
  import("../pages/store-security/restrictions/Restrictions").then((m) => ({
    default: m.Restrictions,
  })),
);
const PolicyWarningProtection = lazy(() =>
  import(
    "../pages/store-security/policy-warning-protection/PolicyWarningProtection"
  ).then((m) => ({
    default: m.PolicyWarningProtection,
  })),
);
const BlockedAsins = lazy(() =>
  import("../pages/store-security/blocked-asins/BlockedAsins").then((m) => ({
    default: m.BlockedAsins,
  })),
);
const Extension = lazy(() =>
  import("../pages/extension/Extension").then((m) => ({
    default: m.Extension,
  })),
);
const Product = lazy(() =>
  import("../pages/product/Product").then((m) => ({ default: m.Product })),
);
const CompetitionSettings = lazy(() =>
  import("../pages/stores/competition-settings/CompetitionSettings").then(
    (m) => ({ default: m.CompetitionSettings }),
  ),
);
const Orders = lazy(() =>
  import("../pages/orders/Orders").then((m) => ({ default: m.Orders })),
);
const UnprocessedEvents = lazy(() =>
  import("../pages/unprocessed-events/UnprocessedEvents").then((m) => ({
    default: m.UnprocessedEvents,
  })),
);
const OrderDetails = lazy(() =>
  import("../pages/order-details/OrderDetails").then((m) => ({
    default: m.OrderDetails,
  })),
);
const MailNotificationSettings = lazy(() =>
  import(
    "../pages/stores/mail-notification-settings/MailNotificationSettings"
  ).then((m) => ({
    default: m.MailNotificationSettings,
  })),
);
const ServiceSelection = lazy(() =>
  import("../pages/warehouse/ServiceSelection").then((m) => ({
    default: m.ServiceSelection,
  })),
);
const Maintenance = lazy(() =>
  import("../pages/maintenance/Maintenance").then((m) => ({
    default: m.Maintenance,
  })),
);
const Dashboard = lazy(() =>
  import("../pages/dashboard/Dashboard").then((m) => ({
    default: m.Dashboard,
  })),
);
const OrdersReport = lazy(() =>
  import("../pages/reports/OrdersReport").then((m) => ({
    default: m.OrdersReport,
  })),
);
const EasterGame = lazy(() =>
  import("../pages/easter-game/EasterGame").then((m) => ({
    default: m.EasterGame,
  })),
);

const PageFallback = () => <SyncrosaleLoader />;

const SyncrosaleRouter = () => {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route
          element={
            <Authenticated
              key="authenticated-inner"
              fallback={<Navigate to="/login" replace={true} />}
            >
              <SyncrosaleLayout>
                <ErrorBoundary>
                  <Suspense fallback={<PageFallback />}>
                    <Outlet />
                  </Suspense>
                </ErrorBoundary>
              </SyncrosaleLayout>
            </Authenticated>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="order">
            <Route index element={<Orders />} />
            <Route path="unprocessed-events" element={<UnprocessedEvents />} />
          </Route>
          <Route path="order-detail/:orderId" element={<OrderDetails />} />
          <Route path="inventory">
            <Route path="" element={<Inventory />} />
            <Route path="product/:asin" element={<Product />} />
          </Route>

          <Route path="store">
            <Route path="create" element={<CreateStore />} />
            <Route path="store-settings" element={<StoreSettings />} />
            <Route path="pricing-settings" element={<PricingSettings />} />
            <Route
              path="mail-notification"
              element={<MailNotificationSettings />}
            />
            <Route
              path="competition-settings"
              element={<CompetitionSettings />}
            />
          </Route>

          <Route path="store-security">
            <Route path="restrictions" element={<Restrictions />} />
            <Route path="filters" element={<Filters />} />
            <Route
              path="policy-warning-protection"
              element={<PolicyWarningProtection />}
            />
            <Route path="blocked-asins" element={<BlockedAsins />} />
            <Route path="*" element={<ErrorComponent />} />
          </Route>

          <Route path="extension" element={<Extension />} />
          <Route path="reports" element={<OrdersReport />} />
          <Route path="warehouse" element={<ServiceSelection />} />
          <Route path="easter-game" element={<EasterGame />} />
        </Route>
        <Route path="/maintenance" element={<Maintenance />} />
        <Route
          element={
            <Authenticated key="authenticated-outer" fallback={<Outlet />}>
              <Navigate to="/" replace={true} />
            </Authenticated>
          }
        >
          <Route path="/login" element={<Login />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default SyncrosaleRouter;
